import React, { useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Checkbox, FormControl } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { saveAs } from "file-saver";
import Modal from "react-responsive-modal";

const STATUS_OPTIONS = [
	{ value: "all", label: "All files" },
	{ value: "tagged", label: "Tagged" },
	{ value: "title", label: "Has title" },
	{ value: "description", label: "Has description" },
	{ value: "complete", label: "Tags + title + description" },
	{ value: "incomplete", label: "Incomplete" },
	{ value: "missing-tags", label: "Missing tags" },
	{ value: "missing-title", label: "Missing title" },
	{ value: "missing-description", label: "Missing description" },
	{ value: "suggested-only", label: "Suggested only" },
	{ value: "empty", label: "No saved metadata" }
];

const SORT_OPTIONS = [
	{ value: "path", label: "Path" },
	{ value: "name", label: "Name" },
	{ value: "mediaType", label: "Media type" },
	{ value: "tagCount", label: "Tag count" },
	{ value: "completeness", label: "Completeness" },
	{ value: "title", label: "Title" },
	{ value: "description", label: "Description" },
	{ value: "timestamp", label: "Timestamp" },
	{ value: "suggestedTagCount", label: "Suggested count" }
];

const REPORT_VISIBLE_BATCH_SIZE = 200;

function normalizeText(value = "") {
	return `${value || ""}`.trim();
}

function getRowSearchText(row) {
	return [
		row.name,
		row.path,
		row.mediaType,
		row.title,
		row.description,
		...(row.primaryTags || []),
		...(row.suggestedTags || [])
	].join(" ").toLowerCase();
}

function matchesStatus(row, statusFilter) {
	switch (statusFilter) {
		case "tagged":
			return row.tagged;
		case "title":
			return row.hasTitle;
		case "description":
			return row.hasDescription;
		case "complete":
			return row.isComplete;
		case "incomplete":
			return !row.isComplete;
		case "missing-tags":
			return !row.tagged;
		case "missing-title":
			return !row.hasTitle;
		case "missing-description":
			return !row.hasDescription;
		case "suggested-only":
			return row.suggestedOnly;
		case "empty":
			return !row.hasPrimaryMetadata;
		default:
			return true;
	}
}

function compareRows(a, b, sortKey, sortDir) {
	const dir = sortDir === "desc" ? -1 : 1;
	const getValue = (row) => {
		switch (sortKey) {
			case "tagCount":
				return row.primaryTags.length;
			case "suggestedTagCount":
				return row.suggestedTags.length;
			case "completeness":
				return row.completeness;
			case "timestamp":
				return row.timestampValue || 0;
			case "title":
				return row.title.toLowerCase();
			case "description":
				return row.description.toLowerCase();
			case "mediaType":
				return row.mediaType.toLowerCase();
			case "name":
				return row.name.toLowerCase();
			case "path":
			default:
				return row.path.toLowerCase();
		}
	};
	const left = getValue(a);
	const right = getValue(b);
	if (sortKey === "timestamp") {
		if (!left && !right) {
			return a.path.localeCompare(b.path);
		}
		if (!left) {
			return 1;
		}
		if (!right) {
			return -1;
		}
		return left === right ? a.path.localeCompare(b.path) : (left - right) * dir;
	}
	if (typeof left === "number" && typeof right === "number") {
		return left === right ? a.path.localeCompare(b.path) : (left - right) * dir;
	}
	return `${left}`.localeCompare(`${right}`, undefined, { numeric: true, sensitivity: "base" }) * dir;
}

function csvValue(value) {
	const normalized = `${value ?? ""}`;
	return `"${normalized.replace(/"/g, "\"\"")}"`;
}

function exportRows(rows) {
	const headers = [
		"Name",
		"Path",
		"Media Type",
		"Tagged",
		"Tag Count",
		"Tags",
		"Has Title",
		"Title",
		"Has Description",
		"Description",
		"Timestamp",
		"Suggested Count",
		"Suggested Tags",
		"Complete"
	];
	const body = rows.map((row) => [
		row.name,
		row.path,
		row.mediaType,
		row.tagged ? "Yes" : "No",
		row.primaryTags.length,
		row.primaryTags.join("; "),
		row.hasTitle ? "Yes" : "No",
		row.title,
		row.hasDescription ? "Yes" : "No",
		row.description,
		row.timestamp,
		row.suggestedTags.length,
		row.suggestedTags.join("; "),
		row.isComplete ? "Yes" : "No"
	]);
	const csv = [headers, ...body].map((row) => row.map(csvValue).join(",")).join("\n");
	saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), `drive-metadata-report-${new Date().toISOString().slice(0, 10)}.csv`);
}

function renderTag(tag, className, onClick) {
	if (!onClick) {
		return (
			<span key={tag} className={className} title={tag}>
				{tag}
			</span>
		);
	}
	return (
		<button key={tag} type="button" className={className} onClick={() => onClick(tag)} title={tag}>
			{tag}
		</button>
	);
}

export default function DriveMetadataReportModal({
	open,
	onClose,
	items = [],
	onEditMetadata,
	onRefreshAll,
	scope = "current",
	scopeLabel = "Drive",
	includeSubfolders = false,
	includeFilesWithoutMetadata = false,
	onScopeChange,
	onIncludeSubfoldersChange,
	onIncludeFilesWithoutMetadataChange,
	refreshDisabled = false,
	loading = false
}) {
	const [query, setQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [mediaTypeFilter, setMediaTypeFilter] = useState("all");
	const [tagFilter, setTagFilter] = useState("all");
	const [sortKey, setSortKey] = useState("path");
	const [sortDir, setSortDir] = useState("asc");
	const [visibleLimit, setVisibleLimit] = useState(REPORT_VISIBLE_BATCH_SIZE);

	const stats = useMemo(() => {
		const total = items.length;
		const tagged = items.filter((row) => row.tagged).length;
		const missingTags = items.filter((row) => !row.tagged).length;
		const withTitle = items.filter((row) => row.hasTitle).length;
		const withDescription = items.filter((row) => row.hasDescription).length;
		const complete = items.filter((row) => row.isComplete).length;
		const suggestedOnly = items.filter((row) => row.suggestedOnly).length;
		const empty = items.filter((row) => !row.hasPrimaryMetadata).length;
		return { total, tagged, missingTags, withTitle, withDescription, complete, suggestedOnly, empty };
	}, [items]);

	const mediaTypeOptions = useMemo(() => {
		return Array.from(new Set(items.map((row) => row.mediaType || "file"))).sort((a, b) => a.localeCompare(b));
	}, [items]);

	const tagOptions = useMemo(() => {
		const tags = new Set();
		items.forEach((row) => (row.primaryTags || []).forEach((tag) => tags.add(tag)));
		return Array.from(tags).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
	}, [items]);

	const filteredRows = useMemo(() => {
		const normalizedQuery = normalizeText(query).toLowerCase();
		return items
			.filter((row) => matchesStatus(row, statusFilter))
			.filter((row) => mediaTypeFilter === "all" || row.mediaType === mediaTypeFilter)
			.filter((row) => tagFilter === "all" || (row.primaryTags || []).some((tag) => tag.toLowerCase() === tagFilter.toLowerCase()))
			.filter((row) => !normalizedQuery || getRowSearchText(row).includes(normalizedQuery))
			.slice()
			.sort((a, b) => compareRows(a, b, sortKey, sortDir));
	}, [items, mediaTypeFilter, query, sortDir, sortKey, statusFilter, tagFilter]);

	useEffect(() => {
		setVisibleLimit(REPORT_VISIBLE_BATCH_SIZE);
	}, [includeFilesWithoutMetadata, includeSubfolders, items.length, mediaTypeFilter, open, query, scope, sortDir, sortKey, statusFilter, tagFilter]);

	const visibleRows = filteredRows.slice(0, visibleLimit);
	const scopeSummary = scope === "all"
		? "All Drive"
		: `${scopeLabel || "Drive"}${includeSubfolders ? " + subfolders" : ""}`;
	const reportFileLabel = includeFilesWithoutMetadata ? "files" : "metadata files";

	const setStatFilter = (nextFilter) => {
		setStatusFilter(nextFilter);
	};

	return (
		<ModalReportWrapper open={open} onClose={onClose}>
			<div className="drive-metadata-report-shell">
				<div className="drive-metadata-report-header">
					<div>
						<h4>Metadata Report</h4>
						<div className="drive-metadata-report-count">
							{loading ? "Refreshing Drive metadata..." : `${filteredRows.length} of ${items.length} ${reportFileLabel}`}
							<span className="drive-metadata-report-scope-summary">{scopeSummary}</span>
						</div>
					</div>
					<div className="drive-metadata-report-header-actions">
						<Button onClick={onRefreshAll} disabled={refreshDisabled}>
							<FontAwesomeIcon icon="sync" /> {loading ? "Refreshing..." : "Refresh"}
						</Button>
						<Button onClick={() => exportRows(filteredRows)} disabled={filteredRows.length === 0}>
							<FontAwesomeIcon icon="download" /> CSV
						</Button>
					</div>
				</div>

				<div className="drive-metadata-report-stats">
					<button type="button" onClick={() => setStatFilter("all")} className={statusFilter === "all" ? "active" : ""}>
						<span>{stats.total}</span>
						All
					</button>
					<button type="button" onClick={() => setStatFilter("tagged")} className={statusFilter === "tagged" ? "active" : ""}>
						<span>{stats.tagged}</span>
						Tagged
					</button>
					<button type="button" onClick={() => setStatFilter("missing-tags")} className={statusFilter === "missing-tags" ? "active" : ""}>
						<span>{stats.missingTags}</span>
						Missing Tags
					</button>
					<button type="button" onClick={() => setStatFilter("title")} className={statusFilter === "title" ? "active" : ""}>
						<span>{stats.withTitle}</span>
						Title
					</button>
					<button type="button" onClick={() => setStatFilter("description")} className={statusFilter === "description" ? "active" : ""}>
						<span>{stats.withDescription}</span>
						Description
					</button>
					<button type="button" onClick={() => setStatFilter("complete")} className={statusFilter === "complete" ? "active" : ""}>
						<span>{stats.complete}</span>
						Complete
					</button>
					<button type="button" onClick={() => setStatFilter("suggested-only")} className={statusFilter === "suggested-only" ? "active" : ""}>
						<span>{stats.suggestedOnly}</span>
						Suggested
					</button>
					<button type="button" onClick={() => setStatFilter("empty")} className={statusFilter === "empty" ? "active" : ""}>
						<span>{stats.empty}</span>
						Empty
					</button>
				</div>

				<div className="drive-metadata-report-filters">
					<FormControl componentClass="select" value={scope} onChange={(event) => onScopeChange && onScopeChange(event.target.value)}>
						<option value="current">Current Folder</option>
						<option value="all">All Drive</option>
					</FormControl>
					<Checkbox
						className="drive-metadata-report-scope-toggle"
						checked={includeSubfolders}
						disabled={scope !== "current"}
						onChange={(event) => onIncludeSubfoldersChange && onIncludeSubfoldersChange(event.target.checked)}
					>
						Include subfolders
					</Checkbox>
					<Checkbox
						className="drive-metadata-report-scope-toggle"
						checked={includeFilesWithoutMetadata}
						onChange={(event) => onIncludeFilesWithoutMetadataChange && onIncludeFilesWithoutMetadataChange(event.target.checked)}
					>
						Include files without metadata
					</Checkbox>
					<FormControl
						type="text"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Search name, path, tags, title, description"
					/>
					<FormControl componentClass="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
						{STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
					</FormControl>
					<FormControl componentClass="select" value={mediaTypeFilter} onChange={(event) => setMediaTypeFilter(event.target.value)}>
						<option value="all">All media</option>
						{mediaTypeOptions.map((mediaType) => <option key={mediaType} value={mediaType}>{mediaType}</option>)}
					</FormControl>
					<FormControl componentClass="select" value={tagFilter} onChange={(event) => setTagFilter(event.target.value)}>
						<option value="all">All tags</option>
						{tagOptions.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
					</FormControl>
					<FormControl componentClass="select" value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
						{SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>Sort: {option.label}</option>)}
					</FormControl>
					<ButtonGroup>
						<Button active={sortDir === "asc"} onClick={() => setSortDir("asc")} title="Sort ascending">
							<FontAwesomeIcon icon="sort-amount-down" />
						</Button>
						<Button active={sortDir === "desc"} onClick={() => setSortDir("desc")} title="Sort descending">
							<FontAwesomeIcon icon="sort-amount-down" rotation={180} />
						</Button>
					</ButtonGroup>
				</div>

				<div className="drive-metadata-report-table-wrap">
					<table className="drive-metadata-report-table">
						<thead>
							<tr>
								<th>Preview</th>
								<th>File</th>
								<th>State</th>
								<th>Title</th>
								<th>Description</th>
								<th>Saved Tags</th>
								<th>Additional</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{visibleRows.map((row) => (
								<tr key={row.key}>
									<td>
										<div className="drive-metadata-report-thumb">
											{row.thumbnailUrl ? (
												<img src={row.thumbnailUrl} alt={row.name} />
											) : (
												<FontAwesomeIcon icon={row.icon || "file"} />
											)}
										</div>
									</td>
									<td>
										<div className="drive-metadata-report-file-name" title={row.name}>{row.name}</div>
										<div className="drive-metadata-report-file-path" title={row.path}>{row.path}</div>
										{row.timestamp && <div className="drive-metadata-report-file-timestamp" title={row.timestamp}>{row.timestamp}</div>}
										<div className="drive-metadata-report-media">{row.mediaType}</div>
									</td>
									<td>
										<div className="drive-metadata-report-statuses">
											<span className={row.tagged ? "complete" : "missing"}>Tags</span>
											<span className={row.hasTitle ? "complete" : "missing"}>Title</span>
											<span className={row.hasDescription ? "complete" : "missing"}>Description</span>
										</div>
									</td>
									<td>
										<div className="drive-metadata-report-text" title={row.title}>{row.title || "-"}</div>
									</td>
									<td>
										<div className="drive-metadata-report-description" title={row.description}>{row.description || "-"}</div>
									</td>
									<td>
										<div className="drive-metadata-report-tags">
											{row.primaryTags.length > 0
												? row.primaryTags.slice(0, 8).map((tag) => renderTag(tag, "drive-metadata-report-tag", setTagFilter))
												: <span className="drive-metadata-report-muted">No saved tags</span>}
											{row.primaryTags.length > 8 && <span className="drive-metadata-report-muted">+{row.primaryTags.length - 8}</span>}
										</div>
									</td>
									<td>
										<div className="drive-metadata-report-tags">
											{row.suggestedTags.length > 0
												? row.suggestedTags.slice(0, 5).map((tag) => renderTag(tag, "drive-metadata-report-suggested-tag"))
												: <span className="drive-metadata-report-muted">None</span>}
											{row.suggestedTags.length > 5 && <span className="drive-metadata-report-muted">+{row.suggestedTags.length - 5}</span>}
										</div>
									</td>
									<td>
										<Button bsSize="small" onClick={() => onEditMetadata && onEditMetadata(row.resource)}>
											<FontAwesomeIcon icon="tags" /> Edit
										</Button>
									</td>
								</tr>
							))}
							{visibleRows.length < filteredRows.length && (
								<tr>
									<td colSpan={8}>
										<div className="drive-metadata-report-load-more">
											<Button onClick={() => setVisibleLimit((currentLimit) => currentLimit + REPORT_VISIBLE_BATCH_SIZE)}>
												Show {Math.min(REPORT_VISIBLE_BATCH_SIZE, filteredRows.length - visibleRows.length)} more
											</Button>
											<span>{visibleRows.length} of {filteredRows.length} shown</span>
										</div>
									</td>
								</tr>
							)}
							{filteredRows.length === 0 && (
								<tr>
									<td colSpan={8}>
										<div className="drive-metadata-report-empty">{loading ? "Refreshing Drive metadata..." : "No files match the current report filters."}</div>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</ModalReportWrapper>
	);
}

function ModalReportWrapper({ open, onClose, children }) {
	return (
		<Modal
			open={open}
			onClose={onClose}
			classNames={{ overlay: "drive-metadata-report-overlay", modal: "drive-metadata-report-modal" }}
			styles={{
				overlay: {
					padding: 0,
					margin: 0,
					display: "block",
					overflow: "hidden"
				},
				modal: {
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					width: "100%",
					height: "100%",
					maxWidth: "none",
					maxHeight: "none",
					margin: 0,
					padding: 0,
					borderRadius: 0,
					boxSizing: "border-box"
				}
			}}
			closeButton
		>
			{children}
		</Modal>
	);
}
